// MissionExecutionEngine.ts — Sprint 076: Mission Execution Engine™
// Build 1.70.0
//
// Single responsibility: track every opportunity from discovery until payment.
// Pure TypeScript + localStorage. Zero external dependencies.
//
// Future database persistence is possible without engine rewrite:
// replace loadExecutionState() + saveExecutionState() with API calls
// — all calling code remains unchanged.

// ─── Execution Status ─────────────────────────────────────────────────────────

export type ExecutionStatus =
  | 'DISCOVERED'
  | 'REVIEWING'
  | 'STARTED'
  | 'APPLIED'
  | 'WAITING'
  | 'INTERVIEW'
  | 'APPROVED'
  | 'WORKING'
  | 'SUBMITTED'
  | 'PAID'
  | 'COMPLETED'
  | 'ARCHIVED';

// ─── Status metadata ─────────────────────────────────────────────────────────

export const STATUS_LABELS: Record<ExecutionStatus, string> = {
  DISCOVERED: 'Discovered',
  REVIEWING:  'Reviewing',
  STARTED:    'Started',
  APPLIED:    'Applied',
  WAITING:    'Waiting',
  INTERVIEW:  'Interview',
  APPROVED:   'Approved',
  WORKING:    'Working',
  SUBMITTED:  'Submitted',
  PAID:       'Paid',
  COMPLETED:  'Completed',
  ARCHIVED:   'Archived',
};

export const STATUS_NEXT_ACTIONS: Record<ExecutionStatus, string> = {
  DISCOVERED: 'Open workspace to review this opportunity.',
  REVIEWING:  'Click START APPLICATION to begin.',
  STARTED:    'Complete and submit the application.',
  APPLIED:    'Wait for company response. Follow up after 5 days.',
  WAITING:    'Follow up if no response received.',
  INTERVIEW:  'Prepare and complete the interview.',
  APPROVED:   'Begin the assigned work.',
  WORKING:    'Complete deliverables and submit work.',
  SUBMITTED:  'Await payment confirmation from platform.',
  PAID:       'Confirm payment received. Mark as completed.',
  COMPLETED:  'Mission complete — no further action required.',
  ARCHIVED:   'This opportunity has been archived.',
};

// Ordered for timeline display. ARCHIVED excluded from standard flow.
export const TIMELINE_STATUSES: ExecutionStatus[] = [
  'DISCOVERED',
  'REVIEWING',
  'STARTED',
  'APPLIED',
  'WAITING',
  'INTERVIEW',
  'APPROVED',
  'WORKING',
  'SUBMITTED',
  'PAID',
  'COMPLETED',
];

// ─── Status colour tokens ─────────────────────────────────────────────────────
// Tailwind utility strings for badge colouring in UI components.

export const STATUS_COLORS: Record<ExecutionStatus, { bg: string; text: string; border: string }> = {
  DISCOVERED: { bg: 'bg-foreground/8',  text: 'text-foreground/45',  border: 'border-border/25'          },
  REVIEWING:  { bg: 'bg-primary/10',    text: 'text-primary/80',      border: 'border-primary/30'         },
  STARTED:    { bg: 'bg-cyan-400/10',   text: 'text-cyan-400/80',     border: 'border-cyan-400/30'        },
  APPLIED:    { bg: 'bg-indigo-400/10', text: 'text-indigo-400/80',   border: 'border-indigo-400/30'      },
  WAITING:    { bg: 'bg-amber-400/10',  text: 'text-amber-400/80',    border: 'border-amber-400/30'       },
  INTERVIEW:  { bg: 'bg-orange-400/10', text: 'text-orange-400/80',   border: 'border-orange-400/30'      },
  APPROVED:   { bg: 'bg-teal-400/10',   text: 'text-teal-400/80',     border: 'border-teal-400/30'        },
  WORKING:    { bg: 'bg-green-400/10',  text: 'text-green-400/80',    border: 'border-green-400/30'       },
  SUBMITTED:  { bg: 'bg-purple-400/10', text: 'text-purple-400/80',   border: 'border-purple-400/30'      },
  PAID:       { bg: 'bg-emerald-400/10', text: 'text-emerald-400/80', border: 'border-emerald-400/30'     },
  COMPLETED:  { bg: 'bg-primary/15',    text: 'text-primary',         border: 'border-primary/40'         },
  ARCHIVED:   { bg: 'bg-foreground/5',  text: 'text-foreground/30',   border: 'border-border/15'          },
};

// ─── Interfaces ───────────────────────────────────────────────────────────────

export interface StatusEntry {
  status:     ExecutionStatus;
  enteredAt:  string;        // ISO 8601
  exitedAt:   string | null;
  durationMs: number | null;
}

export interface ExecutionRecord {
  opportunityId:          number;
  company:                string;
  platform:               string;
  currentStatus:          ExecutionStatus;
  /** ISO — when currentStatus was entered. */
  dateEnteredStatus:      string;
  statusHistory:          StatusEntry[];
  totalMissionDurationMs: number;
  expectedPaymentDate:    string | null;
  actualPaymentDate:      string | null;
  expectedPayout:         number | null;
  actualPayout:           number | null;
  followUpRequired:       boolean;
  nextAction:             string | null;
  notes:                  string | null;
}

export interface MissionExecutionState {
  records:     Record<number, ExecutionRecord>;
  lastUpdated: string;
}

export interface ActiveMissionCounts {
  started:        number;
  applied:        number;
  waiting:        number;  // WAITING + INTERVIEW
  working:        number;  // APPROVED + WORKING
  paymentPending: number;  // SUBMITTED + PAID
  completedToday: number;
}

// ─── Storage ─────────────────────────────────────────────────────────────────

const STORAGE_KEY = 'nomad_execution_engine_v1';

export function loadExecutionState(): MissionExecutionState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return emptyState();
    return (JSON.parse(raw) as MissionExecutionState) ?? emptyState();
  } catch {
    return emptyState();
  }
}

export function saveExecutionState(state: MissionExecutionState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      ...state,
      lastUpdated: new Date().toISOString(),
    }));
    // LIVE PLATFORM: announce the mutation so subscribed screens update
    // without polling (legacy bridge mirrors this onto the core bus).
    window.dispatchEvent(new CustomEvent('nomad:data-changed', { detail: { domain: 'execution' } }));
  } catch {
    // localStorage may be unavailable — fail silently
  }
}

function emptyState(): MissionExecutionState {
  return { records: {}, lastUpdated: new Date().toISOString() };
}

// ─── Record management ────────────────────────────────────────────────────────

/** Returns an existing record or null. Read-only — does not write to storage. */
export function getRecord(opportunityId: number): ExecutionRecord | null {
  return loadExecutionState().records[opportunityId] ?? null;
}

/**
 * Returns an existing record, or creates and persists a new DISCOVERED record.
 * Safe to call multiple times — idempotent if record already exists.
 */
export function getOrCreateRecord(
  opportunityId: number,
  company: string,
  platform: string,
  expectedPayout?: number | null,
): ExecutionRecord {
  const state = loadExecutionState();
  if (state.records[opportunityId]) {
    return state.records[opportunityId];
  }
  const record = makeNewRecord(opportunityId, company, platform, expectedPayout);
  state.records[opportunityId] = record;
  saveExecutionState(state);
  return record;
}

/**
 * Transitions an opportunity to a new status.
 * - Idempotent: if already in newStatus, returns the record unchanged.
 * - Closes the current open StatusEntry and opens a new one.
 * - Creates a DISCOVERED record if none exists.
 * - Returns the updated ExecutionRecord.
 */
export function transitionStatus(
  opportunityId: number,
  newStatus: ExecutionStatus,
  company  = '',
  platform = '',
  expectedPayout?: number | null,
): ExecutionRecord {
  const state  = loadExecutionState();
  const record = state.records[opportunityId]
    ?? makeNewRecord(opportunityId, company, platform, expectedPayout);

  // Idempotent — same status → update company/platform if blank, then persist
  if (record.currentStatus === newStatus) {
    if (!record.company && company)   record.company  = company;
    if (!record.platform && platform) record.platform = platform;
    state.records[opportunityId] = record;
    saveExecutionState(state);
    return record;
  }

  const now   = new Date().toISOString();
  const nowMs = Date.now();

  // Close the current open StatusEntry
  const lastEntry = record.statusHistory.at(-1);
  if (lastEntry && lastEntry.exitedAt === null) {
    const enteredMs       = new Date(lastEntry.enteredAt).getTime();
    lastEntry.exitedAt    = now;
    lastEntry.durationMs  = nowMs - enteredMs;
    record.totalMissionDurationMs += lastEntry.durationMs;
  }

  // Open new StatusEntry
  record.statusHistory.push({
    status:    newStatus,
    enteredAt: now,
    exitedAt:  null,
    durationMs: null,
  });
  record.currentStatus     = newStatus;
  record.dateEnteredStatus = now;
  record.nextAction        = STATUS_NEXT_ACTIONS[newStatus];
  if (company && !record.company)   record.company  = company;
  if (platform && !record.platform) record.platform = platform;

  state.records[opportunityId] = record;
  saveExecutionState(state);
  return record;
}

function makeNewRecord(
  opportunityId: number,
  company: string,
  platform: string,
  expectedPayout?: number | null,
): ExecutionRecord {
  const now = new Date().toISOString();
  return {
    opportunityId,
    company,
    platform,
    currentStatus:          'DISCOVERED',
    dateEnteredStatus:      now,
    statusHistory:          [{ status: 'DISCOVERED', enteredAt: now, exitedAt: null, durationMs: null }],
    totalMissionDurationMs: 0,
    expectedPaymentDate:    null,
    actualPaymentDate:      null,
    expectedPayout:         expectedPayout ?? null,
    actualPayout:           null,
    followUpRequired:       false,
    nextAction:             STATUS_NEXT_ACTIONS['DISCOVERED'],
    notes:                  null,
  };
}

// ─── Active Mission Counts ────────────────────────────────────────────────────

export function getActiveMissionCounts(state: MissionExecutionState): ActiveMissionCounts {
  const records  = Object.values(state.records);
  const todayISO = new Date().toISOString().slice(0, 10);

  return {
    started:        records.filter(r => r.currentStatus === 'STARTED').length,
    applied:        records.filter(r => r.currentStatus === 'APPLIED').length,
    waiting:        records.filter(r => r.currentStatus === 'WAITING' || r.currentStatus === 'INTERVIEW').length,
    working:        records.filter(r => r.currentStatus === 'WORKING'  || r.currentStatus === 'APPROVED').length,
    paymentPending: records.filter(r => r.currentStatus === 'SUBMITTED' || r.currentStatus === 'PAID').length,
    completedToday: records.filter(r =>
      r.currentStatus === 'COMPLETED' &&
      r.dateEnteredStatus.slice(0, 10) === todayISO,
    ).length,
  };
}

// ─── Intelligence adjustments ─────────────────────────────────────────────────
//
// Consumed by TopFiveMission via Home.tsx — ADDITIVE on combinedRankingScore + learningBonus.
// Mission Director is NOT touched (per Sprint 076 constraints).
//
// Rule 1: COMPLETED → −200  (never re-recommend a finished mission)
// Rule 2: ARCHIVED after APPLIED/WAITING/INTERVIEW → −50  (rejected; reduce visibility)
//         ARCHIVED earlier → −15  (abandoned; slight reduction)
// Rule 3: Platform currently paying well → +20  (has PAID or COMPLETED records)

export function computeExecutionAdjustments(
  allOpps: { id: number; companyName?: string | null }[],
  state: MissionExecutionState,
): Map<number, number> {
  const adjustments = new Map<number, number>();

  // Collect platforms with successful payouts
  const payingPlatforms = new Set<string>();
  for (const record of Object.values(state.records)) {
    if (record.currentStatus === 'PAID' || record.currentStatus === 'COMPLETED') {
      payingPlatforms.add(normPlatform(record.company));
    }
  }

  for (const opp of allOpps) {
    const record   = state.records[opp.id];
    const platform = normPlatform(opp.companyName ?? '');
    let   adj      = 0;

    if (record) {
      if (record.currentStatus === 'COMPLETED') {
        // Rule 1: never recommend completed missions
        adj = -200;
      } else if (record.currentStatus === 'ARCHIVED') {
        // Rule 2: rejected vs self-archived
        const wasRejected = record.statusHistory.some(
          e => e.status === 'APPLIED' || e.status === 'WAITING' || e.status === 'INTERVIEW',
        );
        adj = wasRejected ? -50 : -15;
      }
    }

    // Rule 3: boost for platforms currently paying well (only if not already penalised)
    if (adj === 0 && platform && payingPlatforms.has(platform)) {
      adj = 20;
    }

    if (adj !== 0) {
      adjustments.set(opp.id, adj);
    }
  }

  return adjustments;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Returns the number of whole days the record has been in its current status. */
export function daysInCurrentStatus(record: ExecutionRecord): number {
  const enteredMs = new Date(record.dateEnteredStatus).getTime();
  return Math.floor((Date.now() - enteredMs) / 86_400_000);
}

/** Returns the index of a status in TIMELINE_STATUSES, or -1 for ARCHIVED. */
export function timelineIndex(status: ExecutionStatus): number {
  return TIMELINE_STATUSES.indexOf(status);
}

function normPlatform(name: string): string {
  return name.toLowerCase().trim();
}
